import { useMemo } from 'react';

import { useServiceAuditsForStep } from '@commenda/api/agent-platform/service-auditor/useServiceAuditsForStep';
import { TWorkflowExecution } from '@commenda/types';
import { TaskParty } from '@prisma/client';

import { ServiceAuditorStepHelper } from '../../../workflow/helpers/ServiceAuditorStepHelper';
import { ServiceAuditRunStatusHelper } from '../helpers/ServiceAuditRunStatusHelper';

interface IUseServiceAuditStepStatusParams {
  auditorStepId: string | null;
  role: TaskParty;
  workflowExecution: TWorkflowExecution | undefined;
}

/** The newest audit of an auditor step, for reviewers only; null for everyone else or before any audit. */
export function useServiceAuditStepStatus({
  auditorStepId,
  role,
  workflowExecution,
}: Readonly<IUseServiceAuditStepStatusParams>) {
  const canSeeAuditDetails =
    ServiceAuditRunStatusHelper.canSeeAuditDetails(role);
  const definition = workflowExecution?.WorkflowTemplateVersion?.definition;
  const stepExecutions = workflowExecution?.runtime?.stepExecutions;
  const watchStepId = useMemo(
    () =>
      auditorStepId
        ? ServiceAuditorStepHelper.findWatchStepId(definition, auditorStepId)
        : null,
    [definition, auditorStepId]
  );

  // Every caller for one step shares this key, so SWR makes one request.
  const {
    audits,
    isLoading,
    error: auditListError,
  } = useServiceAuditsForStep(
    {
      workflowExecutionId: workflowExecution?.id,
      stepId: auditorStepId,
      enabled: canSeeAuditDetails,
    },
    {
      refreshIntervalMs:
        ServiceAuditRunStatusHelper.getAuditListRefreshIntervalMs(
          stepExecutions,
          [auditorStepId, watchStepId]
        ),
    }
  );

  // A 403 or 404 is the neutral view, not a load error.
  const isNeutralView =
    ServiceAuditRunStatusHelper.isNeutralViewError(auditListError);
  const isReviewerView =
    canSeeAuditDetails && auditorStepId !== null && !isNeutralView;
  const newestAudit = isReviewerView ? (audits[0] ?? null) : null;

  return {
    isReviewerView,
    newestAudit,
    auditStatus: newestAudit?.displayStatus ?? null,
    watchStepId,
    isLoading,
    error: isNeutralView ? undefined : auditListError,
  };
}
